import React, { useState, useEffect, useRef } from 'react';
import BookForm from '../../components/home/FormComponents/BookForm';
import BookSuggestionService from '../../services/home/BookSuggestionManager';
import { motion } from "framer-motion";
import toast from 'react-hot-toast';
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoginPromptModal from "../home/authComponents/LoginPromptModal";



  const BookSuggestionForm = () => {
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const navigate = useNavigate();
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const userId = useSelector((state) => state.auth.user?.userId);

    const [showLoginPrompt, setShowLoginPrompt] = useState(false);

    const [pendingFormData, setPendingFormData] = useState(null);
    const pendingSubmitResolverRef = useRef(null);


const handleSuggestionSubmit = async (formData) => {
  // If not logged in, store the filled form and prompt login,
  // but return a Promise that resolves only after final submission.
if (!isAuthenticated || !userId) {
  setPendingFormData(formData);
  // persist draft across navigation (so full-page login can restore it)
  try {
    sessionStorage.setItem('pendingSuggestion', JSON.stringify(formData));
  } catch (e) {
    console.warn('Could not persist pending suggestion', e);
  }

setShowLoginPrompt(true);
toast("Saved your suggestion. Please login to submit.", { icon: "💾" });
  // return a promise for modal flow (keeps BookForm waiting if modal login used)
  return new Promise((resolve, reject) => {
    pendingSubmitResolverRef.current = { resolve, reject };
  });
}


  setLoading(true);
  setSuccessMessage('');
  setErrorMessage('');

  try {
    const payload = { ...formData, userId };
    await BookSuggestionService.createSuggestion(payload);
    setSuccessMessage('📚 Thank you! Your book suggestion has been submitted.');
    toast.success("📚 Thank you! Your book suggestion has been submitted.");
    return true; // resolves to truthy value for BookForm
  } catch (error) {
    console.error('Suggestion error:', error);
    setErrorMessage('🚨 Something went wrong while submitting your suggestion.');
    toast.error("🚨 Something went wrong while submitting your suggestion.");
    throw error; // reject so BookForm can show failure
  } finally {
    setLoading(false);
  }
};


useEffect(() => {
  if (isAuthenticated && userId && pendingFormData) {
    const submitSaved = async () => {
      setShowLoginPrompt(false); // close modal if open
      setLoading(true);
      setSuccessMessage('');
      setErrorMessage('');
      try {
        const payload = { ...pendingFormData, userId };
        await BookSuggestionService.createSuggestion(payload);
        setSuccessMessage('📚 Thank you! Your book suggestion has been submitted.');
        toast.success("📚 Thank you! Your book suggestion has been submitted.");
        setPendingFormData(null); // clear pending data after success
        sessionStorage.removeItem('pendingSuggestion');

        // resolve the promise returned earlier to BookForm
        if (pendingSubmitResolverRef.current?.resolve) {
          pendingSubmitResolverRef.current.resolve(true);
        }
      } catch (err) {
        console.error('Auto-submit error:', err);
        setErrorMessage('🚨 Something went wrong while submitting your suggestion after login.');
        toast.error("🚨 Something went wrong while submitting your suggestion after login.");

        // reject the promise so BookForm knows submission failed
        if (pendingSubmitResolverRef.current?.reject) {
          pendingSubmitResolverRef.current.reject(err);
        }
      } finally {
        pendingSubmitResolverRef.current = null;
        setLoading(false);
      }
    };

    submitSaved();
  }
}, [isAuthenticated, userId, pendingFormData]);

useEffect(() => {
  // load draft from sessionStorage if present (e.g. after returning from login page)
  const saved = sessionStorage.getItem('pendingSuggestion');
  if (saved) {
    try {
      const data = JSON.parse(saved);
      setPendingFormData(data);
      // don't auto-submit here — wait until user is authenticated (next effect handles that)
    } catch (e) {
      console.warn('Failed to parse saved pending suggestion', e);
      sessionStorage.removeItem('pendingSuggestion');
    }
  }
}, []); // run once on mount



    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start p-0 overflow-x-hidden">
        <div className="w-full">
          <BookForm type="suggestion" onSubmit={handleSuggestionSubmit} />
        </div>
{showLoginPrompt && (
  <LoginPromptModal
    isOpen={showLoginPrompt}
    onClose={() => {
      setShowLoginPrompt(false);
      // user cancelled login -> reject pending promise so BookForm can handle it
      if (pendingSubmitResolverRef.current?.reject) {
        pendingSubmitResolverRef.current.reject(new Error('Login cancelled'));
      }
      pendingSubmitResolverRef.current = null;
      setPendingFormData(null);
    }}
    // you can keep onLoginSuccess prop but the useEffect handles auto-submit
  />
)}

        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 text-gray-600 text-center px-4 text-sm sm:text-base"
          >
            Submitting your suggestion...
          </motion.div>
        )}

        {successMessage && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="mt-4 text-green-600 font-medium text-center px-4 text-sm sm:text-base"
          >
            {successMessage}
          </motion.div>
        )}

        {errorMessage && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="mt-4 text-red-600 font-medium text-center px-4 text-sm sm:text-base"
          >
            {errorMessage}
          </motion.div>
        )}
      </div>
    );
  };

  export default BookSuggestionForm;
